import { FC } from 'react';
import {
  Box,
  Card,
  Checkbox,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { GlobalSpecQuestion } from '../../types';
import { useQuestionnaireResponse } from './questionnaire-response-context';
import { getOptionTitle, getOptionValue } from './wizard-utils';

interface GlobalSpecWizardProps {
  question: GlobalSpecQuestion;
}

/**
 * Renders the question body for a GlobalSpec question — a standalone
 * specification shown as a radio card group (select), a checkbox card group
 * (multiselect), or a plain text/number field.
 * @param root0
 * @param root0.question
 */
export const GlobalSpecWizard: FC<GlobalSpecWizardProps> = ({ question }) => {
  const { catalog, answers, handleAnswerChange, handleNext } =
    useQuestionnaireResponse();
  const rawAnswer = answers[question.id];

  const spec = catalog.specifications.find(
    (s) => s.id === question.specificationId,
  );
  if (!spec) return null;

  const currentAnswer = (rawAnswer as string) || spec.defaultValue || '';
  const currentMultiAnswer: string[] = Array.isArray(rawAnswer)
    ? (rawAnswer as string[])
    : spec.defaultValue
      ? [spec.defaultValue]
      : [];

  const handleMultiToggle = (value: string): void => {
    const next = currentMultiAnswer.includes(value)
      ? currentMultiAnswer.filter((v) => v !== value)
      : [...currentMultiAnswer, value];
    handleAnswerChange(question.id, next);
  };

  const renderOptionCard = (
    value: string,
    title: string,
    description: string | undefined,
    imageUrl: string | undefined,
    isSelected: boolean,
    control: React.ReactNode,
    onClick: () => void,
  ) => (
    <Card
      key={value}
      variant="outlined"
      sx={{
        cursor: 'pointer',
        borderColor: isSelected ? 'primary.main' : 'divider',
        borderWidth: 2,
        outline: '2px solid transparent',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        minHeight: imageUrl ? { xs: 120, md: 160 } : 80,
        '&:hover': {
          outline: '2px solid',
          outlineColor: isSelected ? 'primary.main' : 'divider',
        },
      }}
      onClick={onClick}
    >
      <Stack
        direction="row"
        sx={{
          cursor: 'pointer',
          minHeight: imageUrl ? { xs: 120, md: 160 } : 80,
        }}
      >
        {/* Image */}
        {imageUrl && (
          <Box
            sx={{
              width: { xs: 120, md: 160 },
              minWidth: { xs: 120, md: 160 },
              alignSelf: 'stretch',
              position: 'relative',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={title}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        )}

        {/* Title / Description */}
        <Stack
          sx={{ flex: 1, minWidth: 0, p: 2 }}
          justifyContent="flex-start"
          spacing={0.5}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5">{title}</Typography>
            {control}
          </Stack>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Card>
  );

  return (
    <Stack spacing={3}>
      {spec.inputType === 'select' && spec.options && (
        <RadioGroup
          value={currentAnswer}
          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
        >
          <Stack spacing={2}>
            {spec.options.map((option) => {
              const value = getOptionValue(option);
              const title = getOptionTitle(option);
              const description =
                typeof option === 'object' && 'description' in option
                  ? (option.description as string)
                  : undefined;
              const imageUrl =
                typeof option === 'object' && 'imageUrl' in option
                  ? option.imageUrl
                  : undefined;

              return renderOptionCard(
                value,
                title,
                description,
                imageUrl,
                currentAnswer === value,
                <Radio
                  checked={currentAnswer === value}
                  readOnly
                  sx={{ pointerEvents: 'none' }}
                />,
                () => {
                  if (value !== currentAnswer) {
                    handleAnswerChange(question.id, value);
                    setTimeout(() => handleNext(), 300);
                  }
                },
              );
            })}
          </Stack>
        </RadioGroup>
      )}

      {spec.inputType === 'multiselect' && spec.options && (
        <Stack spacing={2}>
          {spec.options.map((option) => {
            const value = getOptionValue(option);
            const title = getOptionTitle(option);
            const description =
              typeof option === 'object' && 'description' in option
                ? (option.description as string)
                : undefined;
            const imageUrl =
              typeof option === 'object' && 'imageUrl' in option
                ? (option.imageUrl as string)
                : undefined;
            const checked = currentMultiAnswer.includes(value);

            return renderOptionCard(
              value,
              title,
              description,
              imageUrl,
              checked,
              <Checkbox
                checked={checked}
                readOnly
                sx={{ pointerEvents: 'none' }}
              />,
              () => handleMultiToggle(value),
            );
          })}
        </Stack>
      )}

      {spec.inputType === 'text' && (
        <TextField
          fullWidth
          value={currentAnswer}
          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
          placeholder="Enter your answer..."
        />
      )}

      {spec.inputType === 'number' && (
        <TextField
          fullWidth
          type="number"
          value={currentAnswer}
          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
          placeholder="Enter a number..."
        />
      )}
    </Stack>
  );
};
